import { type IntlShape, defineMessages } from 'react-intl';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { ReactNode } from 'react';
import { StaticImageData } from 'next/image';
import phoneSrcAvif from '../../assets/phone.avif';
// download image imports
import downloadDaDk from './images/download/download-da-DK.avif';
import downloadDeDe from './images/download/download-de-DE.avif';
import downloadEnGb from './images/download/download-en-GB.avif';
import downloadEnUS from './images/download/download-en-US.avif';
import downloadEsEs from './images/download/download-es-ES.avif';
import downloadEsMx from './images/download/download-es-MX.avif';
import downloadFrFr from './images/download/download-fr-FR.avif';
import downloadItIt from './images/download/download-it-IT.avif';
import downloadNbNo from './images/download/download-nb-NO.avif';
import downloadNlNl from './images/download/download-nl-NL.avif';
import downloadPlPl from './images/download/download-pl-PL.avif';
import downloadPtBr from './images/download/download-pt-BR.avif';
import downloadPtPt from './images/download/download-pt-PT.avif';
import downloadSvSE from './images/download/download-sv-SE.avif';
// navigate image imports
import navigateDaDk from './images/navigate/navigate-da-DK.avif';
import navigateDeDe from './images/navigate/navigate-de-DE.avif';
import navigateEnGb from './images/navigate/navigate-en-GB.avif';
import navigateEnUS from './images/navigate/navigate-en-US.avif';
import navigateEsEs from './images/navigate/navigate-es-ES.avif';
import navigateEsMx from './images/navigate/navigate-es-MX.avif';
import navigateFrFr from './images/navigate/navigate-fr-FR.avif';
import navigateItIt from './images/navigate/navigate-it-IT.avif';
import navigateNbNo from './images/navigate/navigate-nb-NO.avif';
import navigateNlNl from './images/navigate/navigate-nl-NL.avif';
import navigatePlPl from './images/navigate/navigate-pl-PL.avif';
import navigatePtBr from './images/navigate/navigate-pt-BR.avif';
import navigatePtPt from './images/navigate/navigate-pt-PT.avif';
import navigateSvSE from './images/navigate/navigate-sv-SE.avif';
// liveshare image imports
import liveshareDaDk from './images/liveshare/liveshare-da-DK.avif';
import liveshareDeDe from './images/liveshare/liveshare-de-DE.avif';
import liveshareEnGb from './images/liveshare/liveshare-en-GB.avif';
import liveshareEnUS from './images/liveshare/liveshare-en-US.avif';
import liveshareEsEs from './images/liveshare/liveshare-es-ES.avif';
import liveshareEsMx from './images/liveshare/liveshare-es-MX.avif';
import liveshareFrFr from './images/liveshare/liveshare-fr-FR.avif';
import liveshareItIt from './images/liveshare/liveshare-it-IT.avif';
import liveshareNbNo from './images/liveshare/liveshare-nb-NO.avif';
import liveshareNlNl from './images/liveshare/liveshare-nl-NL.avif';
import livesharePlPl from './images/liveshare/liveshare-pl-PL.avif';
import livesharePtBr from './images/liveshare/liveshare-pt-BR.avif';
import livesharePtPt from './images/liveshare/liveshare-pt-PT.avif';
import liveshareSvSE from './images/liveshare/liveshare-sv-SE.avif';

export type InterstitialVariants = 'generic' | 'download' | 'navigate' | 'liveshare';

const variantAltTextMessages = defineMessages<InterstitialVariants>({
  generic: {
    defaultMessage: 'An app screen shows nearby trails and an option to explore wildflower trails.'
  },
  download: {
    defaultMessage: 'An app screen shows several trail map options available for download.'
  },
  navigate: {
    defaultMessage: 'An app screen shows live stats and GPS navigation on an interactive map.'
  },
  liveshare: {
    defaultMessage: 'A screen shows a trailgoer’s location on a route map, their real-time stats, and a button to cheer them on.'
  }
});

const variantHeaderMessages = defineMessages<InterstitialVariants>({
  generic: {
    defaultMessage: 'AllTrails is better in the app'
  },
  download: {
    defaultMessage: "Download to go where cell service can't"
  },
  navigate: {
    defaultMessage: 'Stay on route and track your travels'
  },
  liveshare: {
    defaultMessage: 'Live share your adventures'
  }
});

const variantImages: Record<InterstitialVariants, Record<LanguageRegionCode, StaticImageData>> = {
  // Use old phone image for now. Likely will be redesigned & use localized imagery.
  generic: {
    ['da-DK']: phoneSrcAvif,
    ['nl-NL']: phoneSrcAvif,
    ['en-GB']: phoneSrcAvif,
    ['en-US']: phoneSrcAvif,
    ['fr-FR']: phoneSrcAvif,
    ['de-DE']: phoneSrcAvif,
    ['it-IT']: phoneSrcAvif,
    ['nb-NO']: phoneSrcAvif,
    ['pl-PL']: phoneSrcAvif,
    ['pt-PT']: phoneSrcAvif,
    ['pt-BR']: phoneSrcAvif,
    ['es-ES']: phoneSrcAvif,
    ['es-MX']: phoneSrcAvif,
    ['sv-SE']: phoneSrcAvif
  },
  download: {
    ['da-DK']: downloadDaDk,
    ['nl-NL']: downloadNlNl,
    ['en-GB']: downloadEnGb,
    ['en-US']: downloadEnUS,
    ['fr-FR']: downloadFrFr,
    ['de-DE']: downloadDeDe,
    ['it-IT']: downloadItIt,
    ['nb-NO']: downloadNbNo,
    ['pl-PL']: downloadPlPl,
    ['pt-PT']: downloadPtPt,
    ['pt-BR']: downloadPtBr,
    ['es-ES']: downloadEsEs,
    ['es-MX']: downloadEsMx,
    ['sv-SE']: downloadSvSE
  },
  navigate: {
    ['da-DK']: navigateDaDk,
    ['nl-NL']: navigateNlNl,
    ['en-GB']: navigateEnGb,
    ['en-US']: navigateEnUS,
    ['fr-FR']: navigateFrFr,
    ['de-DE']: navigateDeDe,
    ['it-IT']: navigateItIt,
    ['nb-NO']: navigateNbNo,
    ['pl-PL']: navigatePlPl,
    ['pt-PT']: navigatePtPt,
    ['pt-BR']: navigatePtBr,
    ['es-ES']: navigateEsEs,
    ['es-MX']: navigateEsMx,
    ['sv-SE']: navigateSvSE
  },
  liveshare: {
    ['da-DK']: liveshareDaDk,
    ['nl-NL']: liveshareNlNl,
    ['en-GB']: liveshareEnGb,
    ['en-US']: liveshareEnUS,
    ['fr-FR']: liveshareFrFr,
    ['de-DE']: liveshareDeDe,
    ['it-IT']: liveshareItIt,
    ['nb-NO']: liveshareNbNo,
    ['pl-PL']: livesharePlPl,
    ['pt-PT']: livesharePtPt,
    ['pt-BR']: livesharePtBr,
    ['es-ES']: liveshareEsEs,
    ['es-MX']: liveshareEsMx,
    ['sv-SE']: liveshareSvSE
  }
};

type InterstitialContent = {
  header: ReactNode;
  altText: string;
  phoneImg: StaticImageData;
};

const getInterstitialContent = (variant: InterstitialVariants, languageRegionCode: LanguageRegionCode, intl: IntlShape): InterstitialContent => ({
  altText: intl.formatMessage(variantAltTextMessages[variant]),
  header: intl.formatMessage(variantHeaderMessages[variant]),
  phoneImg: variantImages[variant][languageRegionCode]
});

export default getInterstitialContent;
